'use client';
import styled from 'styled-components';
import { primary,hover,text } from '@/styles/globalStyleVars';
import { Container } from 'react-bootstrap';
import React, { useRef, useEffect, useState } from 'react';
import reactHtmlParser from "react-html-parser";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import { usePathname } from "next/navigation";
import Select, {components} from 'react-select';

gsap.registerPlugin(ScrollTrigger);

const MyComponent = ({ data }) => {

    const listRef = useRef(null);
    const pathName = usePathname();

    useEffect(() => {
        let scrollTriggerInstance;

        if (listRef.current && typeof window !== 'undefined' && window.innerWidth > 1200) {
            scrollTriggerInstance = ScrollTrigger.create({
                trigger: listRef.current,
                start: "top top",
                endTrigger: ".footer__bottom",
                end: "bottom bottom",
                pin: true,
                pinSpacing: false,
                scrub: true,
                markers: false,
                onEnter: () => {
                    document.body.classList.add('service-menu');
                },
                onLeaveBack: () => {
                    document.body.classList.remove('service-menu');
                },
                onLeave: () => {
                    document.body.classList.remove('service-menu');
                }
            });
            data?.forEach((_, index) => {
                ScrollTrigger.create({
                    trigger: `#section${index + 1}`,
                    start: "top center",
                    end: "bottom center",
                    onEnter: () => handleSectionEnter(index),
                    onEnterBack: () => handleSectionEnter(index),
                    onLeave: () => handleSectionLeave(index),
                    onLeaveBack: () => handleSectionLeave(index),
                });
            });
        }

        // Clean up on unmount
        return () => {
            // Remove the class if it's still present
            document.body.classList.remove('service-menu');

            // Kill the ScrollTrigger instance
            if (scrollTriggerInstance) {
                scrollTriggerInstance.kill();
            }

        };
    }, [data]);

    const handleSectionEnter = (index) => {
        // Remove active class from all links
        listRef?.current?.querySelectorAll('a').forEach((item) => item.classList.remove('active'));

        // Add active class to the current link
        const currentLink = listRef?.current?.querySelector(`a[href='#section${index + 1}']`);
        if (currentLink) {
            currentLink.classList.add('active');
        }
    };

    const handleSectionLeave = (index) => {
        // Optionally handle when the section leaves the viewport
        const currentLink = listRef?.current?.querySelector(`a[href='#section${index + 1}']`);
        if (currentLink) {
            currentLink.classList.remove('active');
        }
    };

    const handleItemClick = (e, index, sectionId) => {
        e.preventDefault();

        // Remove active class from all links
        listRef.current.querySelectorAll('a').forEach((item) => item.classList.remove('active'));

        // Add active class to the clicked link
        e.target.classList.add('active');

        // Scroll to the section within the ScrollSmoother context
        gsap.to(window, {
            scrollTo: { y: sectionId, offsetY: 0 },
            duration: 1
        });
    };

    // Create options for React Select
    const options = data?.map((element, index) => ({
        value: `#section${index + 1}`,
        label: reactHtmlParser(element?.data?.title),
    }));

    const handleSelectChange = (selectedOption) => {
        // Scroll to the selected section
        gsap.to(window, {
            scrollTo: { y: selectedOption.value, offsetY: 0 },
            duration: 1
        });
    };
    const customStyles = {
        dropdownIndicator: (base, state) => ({
            ...base,
            transition: "all .2s ease",
            transform: state.selectProps.menuIsOpen && "rotate(180deg)"
        }),
        control: (base, state) => ({
            ...base,
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            borderRadius: 0,
            paddingLeft: 30,
            paddingRight: 30,
            color: '#FFF',
            height: 60,
            zIndex: 10,
            boxShadow: state.isFocused ? null : null,
            "&:hover": {
                borderColor: `${hover}`
            },
        }),

        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#638297' : '#FFF',
            "&:hover": {
                backgroundColor: "transparent",
                color: `${text}`,
                cursor: 'pointer'
            },
        }),
        indicatorContainer: (base, state) => ({
            ...base,
            transform: state.selectProps.menuIsOpen && "rotate(180deg)"
        })
    };

    const CaretDownIcon = () => {
        return <img src={'/images/static/down.svg'} style={{width: 12, height: 6}}/>;
    };

    const DropdownIndicator = props => {
        return (
            <components.DropdownIndicator {...props}>
                <CaretDownIcon/>
            </components.DropdownIndicator>
        );
    };

    return (
        <StyledComponent ref={listRef}>
            <Container>
                    <div className="for-mobile">
                        <Select
                            styles={customStyles}
                            options={options}
                            onChange={handleSelectChange}
                            placeholder="Select an Option"
                            components={{DropdownIndicator}}
                            isSearchable={false}  // Disable typing
                        />
                    </div>

                    <ul>
                        {data?.map((element, index) => (
                            <li key={index}>
                                <a
                                    href={`#section${index + 1}`}
                                    onClick={(e) => handleItemClick(e, index, `#section${index + 1}`)}
                                >
                                    {reactHtmlParser(element?.data?.title)}
                                </a>
                            </li>
                        ))}
                    </ul>
            </Container>
        </StyledComponent>
    );
};

const StyledComponent = styled.section`
    background-color: ${primary};
    position: relative;
    z-index: 999999999;
    ul {
        display: flex;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
        padding: 0;
        margin: 0;
        list-style: none;

        li {
            flex: 0 0 auto;
            scroll-snap-align: start;
            margin-right: 30px;
            padding: 30px 0;

            a {
                font-size: 18px;
                line-height: 24px;
                color: rgba(255, 255, 255, 0.7);
                font-weight: 500;
                transition: all 0.5s ease-in-out;
                position: relative;
                z-index: 9999999;
                &:hover {
                    color: #fff !important;
                }

                &.active {
                    color: #fff;
                }
            }
        }

        @media (max-width: 767px) {
            display: none;
        }
    }
    
    .for-mobile{
        display: none;
    }
    @media(max-width: 991px){
        .for-mobile{
            display: block;
        }
        ul{
            display: none;
        }
    }
    
    .css-13cymwt-control{
        background-color: ${primary};
        border: none;
    }
    .css-p60uwv-control{
        padding-left: 15px;
        padding-bottom: 15px;
        padding-top: 10px;
        &:focus{
            border: none;
            outline: none !important;
            box-shadow: none;
        }
        &:hover{
            border: none;
        }
    }
    .css-1u9des2-indicatorSeparator{
        display: none;
    }
    .css-1jqq78o-placeholder{
        color: #FFF;
    }
    .css-1dimb5e-singleValue{
        color: #FFF;
    }
    .css-19bb58m{
        color: #FFF; 
        input{
            color: #FFF;
            &::placeholder{
                color: #FFF;
            }
        }
    }
`;

export default MyComponent;
